import React from "react";
import { LegalShell, LegalSection } from "./LegalShell";

export default function Terms() {
  return (
    <LegalShell title="TERMS OF SERVICE" subtitle="The Fine Print">
      <p>
        Welcome to <strong className="text-white">GP Picks</strong> ("we", "us", "the Service"). By creating an account,
        subscribing, or otherwise accessing the Service at gppicks.com, you agree to these Terms of Service ("Terms"). If
        you do not agree, do not use the Service.
      </p>

      <LegalSection title="1. What GP Picks Is (and Isn't)">
        <p>
          GP Picks is a <strong className="text-white">sports statistics and predictive analytics service</strong>.
          We aggregate publicly available sportsbook lines and player performance data, apply a proprietary
          confidence-scoring model, and publish "picks" for informational and entertainment purposes only.
        </p>
        <p>
          <strong className="text-white">GP Picks is NOT a sportsbook, bookmaker, gambling operator, or financial advisor.</strong>
          We do not accept wagers, hold funds, or facilitate betting. We provide information. What you do with that information
          is entirely your decision and your responsibility.
        </p>
      </LegalSection>

      <LegalSection title="2. No Guarantees. Gamble At Your Own Risk.">
        <p className="border-l-2 border-[#FF4500] pl-4 py-2 bg-[#1a1a1a] text-white/90">
          <strong className="text-[#FF4500] uppercase">Important:</strong> <strong>No pick on GP Picks is guaranteed.</strong> Every
          published pick shows a <strong>confidence rating</strong>, which is a probabilistic model output — not a promise, not a
          forecast of a win, and not a recommendation to bet. A confidence rating is nothing more than a mathematical score.
          You are 100% responsible for any bet you place. Any wagers you make based on GP Picks content are made
          <strong> entirely at your own risk</strong>.
        </p>
        <p>
          The confidence rating for a pick — whether it is labeled "Prime", "Strong", or "Lean" — reflects only the model's
          computed probability at a point in time based on line movement, recent stats, and matchup factors. It is not a
          guarantee of any outcome. Even the highest-confidence pick can lose. We make no promises about win rates, ROI,
          hit percentages, streaks, or any specific outcome — implied or explicit.
        </p>
        <p>
          Sports betting involves real financial risk. Only wager amounts you can afford to lose. Never chase losses. If you
          or someone you know has a gambling problem, resources are available:
        </p>
        <ul className="list-disc list-inside pl-2 text-white/70">
          <li>Canada — Responsible Gambling Council: 1-888-230-3505 · responsiblegambling.org</li>
          <li>British Columbia — BC Responsible &amp; Problem Gambling Program: 1-888-795-6111 · bcresponsiblegambling.ca</li>
          <li>United States — National Council on Problem Gambling: 1-800-522-4700 · ncpgambling.org</li>
        </ul>
      </LegalSection>

      <LegalSection title="3. Eligibility & Local Law Compliance">
        <p>
          You must be at least 18 years old (or the age of legal majority in your jurisdiction, whichever is greater) to
          use the Service. In some jurisdictions the required age for gambling-related content is 21. It is your
          responsibility to ensure your use of the Service is legal where you reside.
        </p>
        <p className="border-l-2 border-[#FF4500] pl-4 py-2 bg-[#1a1a1a] text-white/90">
          <strong className="text-[#FF4500] uppercase">Your Responsibility:</strong> You are solely responsible for
          understanding and complying with <strong>all applicable local, provincial/state, national, and international laws
          and regulations</strong> that apply to you — including any laws governing sports predictions, sports wagering,
          age restrictions, and taxes on winnings. If sports predictions or wagering are prohibited or restricted in your
          jurisdiction, you must not use the Service.
        </p>
        <p>
          The Service is not directed to residents of any jurisdiction where the offering of sports predictions is
          prohibited. By accessing the Service, you represent and warrant that (a) you are of legal age, (b) your use
          complies with all laws applicable to you, and (c) you expressly waive any claim against GP Picks arising from
          your own non-compliance with local law.
        </p>
        <p>
          GP Picks does not provide legal advice about whether sports wagering or the use of prediction services is
          legal in your area. Consult local authorities or a lawyer if you are unsure.
        </p>
      </LegalSection>

      <LegalSection title="4. Account & Security">
        <p>
          You are responsible for keeping your credentials confidential and for all activity under your account. Notify us
          immediately of any unauthorized access.
        </p>
        <p>One account per person. No sharing credentials. No bots, scrapers, or programmatic access without prior written consent.</p>
      </LegalSection>

      <LegalSection title="5. Subscriptions, Billing, and Cancellation">
        <p>
          GP Picks Pro is a <strong className="text-white">$5.00 USD/month recurring subscription</strong>, billed monthly to your
          payment method on file via Stripe. Prices exclude any applicable taxes.
        </p>
        <p>
          <strong className="text-white">Auto-renewal:</strong> your subscription renews automatically each month until you cancel.
          Cancel any time from your account portal; access continues through the end of the paid period, and no partial-month
          refunds are issued.
        </p>
        <p>
          <strong className="text-white">Refunds:</strong> we do not offer refunds for partial billing periods. If you believe you
          were charged in error, contact support within 7 days and we will review in good faith.
        </p>
        <p>
          <strong className="text-white">Price changes:</strong> we may adjust pricing with at least 30 days' notice. Continued
          use after the change constitutes acceptance.
        </p>
      </LegalSection>

      <LegalSection title="6. Acceptable Use">
        <p>You agree not to:</p>
        <ul className="list-disc list-inside pl-2 text-white/70">
          <li>Reproduce, redistribute, or publicly display picks except for your personal, non-commercial use.</li>
          <li>Reverse-engineer, scrape, or bulk-download the Service.</li>
          <li>Use the Service to violate any law, including in any jurisdiction where sports predictions are restricted.</li>
          <li>Interfere with the Service, attempt unauthorized access, or introduce malware.</li>
        </ul>
      </LegalSection>

      <LegalSection title="7. Intellectual Property">
        <p>
          All content, branding, models, and code on the Service are owned by GP Picks or its licensors and are protected
          by intellectual property laws. Your subscription grants a limited, non-transferable, revocable license to view
          the content for personal use only.
        </p>
      </LegalSection>

      <LegalSection title="8. Third-Party Data">
        <p>
          Sportsbook lines are sourced from <a href="https://the-odds-api.com" target="_blank" rel="noopener noreferrer" className="text-[#FF4500] hover:underline">The Odds API</a>.
          Player and team statistics are sourced from ESPN's publicly available endpoints. We do not warrant the accuracy,
          completeness, or timeliness of third-party data, and we are not liable for any errors or omissions.
        </p>
      </LegalSection>

      <LegalSection title="9. Disclaimers & Limitation of Liability">
        <p className="uppercase text-xs">
          The Service is provided "as is" and "as available" without warranties of any kind, whether express or implied.
          To the maximum extent permitted by law, GP Picks disclaims all implied warranties of merchantability, fitness
          for a particular purpose, and non-infringement.
        </p>
        <p className="uppercase text-xs">
          In no event shall GP Picks, its affiliates, or agents be liable for any indirect, incidental, special,
          consequential, or punitive damages — including without limitation lost profits, lost wagers, or lost data —
          arising out of or related to your use of the Service. Total aggregate liability is limited to the amount
          you paid us in the preceding twelve (12) months.
        </p>
      </LegalSection>

      <LegalSection title="10. Termination">
        <p>
          We may suspend or terminate your account at any time for violation of these Terms, suspected fraud, or as
          required by law. On termination, your right to access the Service ends immediately.
        </p>
      </LegalSection>

      <LegalSection title="11. Changes to These Terms">
        <p>
          We may update these Terms from time to time. Material changes will be posted here with an updated "Last updated"
          date. Continued use after changes constitutes acceptance.
        </p>
      </LegalSection>

      <LegalSection title="12. Governing Law">
        <p>
          These Terms are governed by the laws of the Province of British Columbia, Canada, without regard to conflict-of-laws
          principles. Any dispute will be resolved exclusively in the courts of British Columbia, Canada.
        </p>
      </LegalSection>

      <LegalSection title="13. Contact">
        <p>Questions or support? Message us on Instagram: <a href="https://instagram.com/gppickdotcom" target="_blank" rel="noopener noreferrer" className="text-[#FF4500] hover:underline">@gppickdotcom</a>.</p>
      </LegalSection>
    </LegalShell>
  );
}
